import { AxiosResponse, Method } from 'axios';
import { SVButtonVariant } from './brandingThemes';
import {
  Breakpoint,
  ButtonProps,
  FilledInputProps,
  GridSize,
  InputProps,
  OutlinedInputProps,
  TableCellProps,
} from '@mui/material';
import { ReactElement } from 'react';
import { SelectOption, InputRules } from './inputs';

export type User = {
  userName?: string;
  userEmail: string;
};

export interface Address {
  line1: string;
  line2: string;
  city: string;
  state: string;
  country?: string;
  postalCode: string;
}

export type OTPFormFields = {
  code: '';
};

export type Money = { currency: string; value: number | string };

export type IdempotentRequest<T> = T & {
  idempotencyKey: string;
};

export type PaginatedResponse<T> = {
  totalRecordCount: number;
  currentPage: number;
  lastPage: number;
  records: T[];
};

export type DateTimeISO = string; //ex: '2024-08-07T17:22:55.124Z'

export type ApiRequest = {
  url: string;
  method: Method;
  name?: 'default' | 'appApi';
  data?: any;
  isAuth?: boolean;
  idempotencyKey?: string;
};

export interface ApiResponse<T> extends AxiosResponse<T, any> {
  isCachedResponse: boolean;
}

export type SVToastButtonData = {
  text: string;
  variant: SVButtonVariant;
  onClick?: (() => Promise<void>) | (() => void);
  closesToast?: boolean;
};

export type SVToastData = {
  content: string;
  title?: string;
  buttons?: SVToastButtonData[];
};

export type SVTableHeader = {
  content: string;
  key: string;
  align?: TableCellProps['align'];
};

export type SVTableRow = {
  key: string | number;
  cells: SVTableCell[];
};

export type SVTableCell = {
  key: string;
  type: 'text' | 'date' | 'amount' | 'link' | 'custom';
  align?: TableCellProps['align'];
} & (
  | {
      type: 'custom';
      content: ReactElement;
    }
  | {
      type: 'amount';
      content: string | number;
    }
  | {
      type: 'link' | 'text' | 'date';
      content: string;
    }
);

export interface SVButtonProps extends Omit<ButtonProps, 'variant'> {
  isLoading?: boolean;
  variant?: SVButtonVariant;
  breadcrumbLabel?: string;
}

export type SVSelectProps = {
  name: string;
  label: string;
  disabled?: boolean;
  options: SelectOption[];
  rules?: InputRules;
  inputProps?:
    | Partial<FilledInputProps>
    | Partial<OutlinedInputProps>
    | Partial<InputProps>
    | undefined;
  breadcrumbLabel?: string;
};

export type MainMenuItem = {
  key: string;
  label: string;
  route: string;
  subpages?: MainMenuItem[];
  activeRoutes: string[];
};

export interface ILottieComponentProps {
  color: string;
}

export enum Status {
  INACTIVE,
  ACTIVE,
}

export enum StatusQueryParam {
  INACTIVE = '0',
  ACTIVE = '1',
}

export type SVLabeledTextProps = {
  label: string;
  text?: string | number;
  isPrimaryColor?: boolean;
  isSmall?: boolean;
  customContent?: ReactElement | null;
};

export type SVSummaryCardItem = SVLabeledTextProps & {
  size?: { [key in Breakpoint]?: GridSize };
};

export type UpdatePasswordFields = {
  password: string;
  confirmPassword: string;
};

export type UpdateEmailFields = {
  uuid: string;
  email: string;
  confirmEmail: string;
  otp: string;
};

export type UpdatePhoneFields = {
  uuid: string;
  phone: string;
  confirmPhone: string;
  otp: string;
};

export interface SearchParamsObject {
  [key: string]: string;
}

export type BreadcrumbItem = {
  label: string;
  href?: string;
  onClick?: () => void;
  isActive?: boolean;
};

export interface ReusableFormFields {
  [key: string]: {
    label: string;
    rules?: InputRules;
  };
}

export type PresignedUrlRequestTypes =
  | 'bank account application'
  | 'external bank account'
  | 'profile avatar'
  | 'business document'
  | 'consumer document';

export type PresignedUrlRequest = {
  type: PresignedUrlRequestTypes;
  uuid: string | null;
  file: string;
};
